import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Environment } from '@global/infrastructure/models/environment';
import { environment } from '@env/environment';
import { AnalyticsService } from '@global/services/analytics/analytics.service';

export abstract class ResourceService<T> {
  protected readonly url: string;
  protected readonly httpOptions: { headers: HttpHeaders };

  constructor(
    protected httpClient: HttpClient,
    protected env: Environment,
    protected endpoint?: string,
  ) {
    const isApp = this.isAppContext();

    this.httpOptions = {
      headers: new HttpHeaders({
        'x-api-key': env.apiKey,
        'x-dlg-brand-name': environment.brand_copy.brandSimple,
        'x-dlg-source': isApp ? 'app' : 'web'
      }),
    };
    this.url = env.apiUrl;
  }

  private isAppContext(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('appID');
  }

  static parseSsoUrl(url: string): string {
    const [scheme, ...rest] = url.split(':');
    return [
      scheme,
      rest
        .join(':')
        .split('/')
        .filter((w) => w)[0],
    ].join('://');
  }

  create(item: T, isSso = false): Observable<T> {
    this.trackApi('request');
    const url = isSso ? ResourceService.parseSsoUrl(this.url) : this.url;
    return this.httpClient
      .post<T>(`${url}/${this.endpoint}`, item, this.httpOptions)
      .pipe(
        map((data) => data),
        tap((data) => this.trackApi('response', data)),
        catchError(this.handleResourceError<T>('create'))
      );
  }

  read(id: number | string, isSso = false): Observable<T> {
    this.trackApi('request');
    const url = isSso ? ResourceService.parseSsoUrl(this.url) : this.url;
    return this.httpClient
      .get(`${url}/${this.endpoint}/${id}`, this.httpOptions)
      .pipe(
        map((data: any) => data as T),
        tap((data) => this.trackApi('response', data)),
        catchError(this.handleResourceError<T>('read'))
      );
  }

  refresh(id: number | string, isSso = false): Observable<T> {
    this.trackApi('request');
    const url = isSso ? ResourceService.parseSsoUrl(this.url) : this.url;
    const endpoint = this.endpoint.replace(/\?app=(stp|ren)/, '');
    return this.httpClient
      .post(`${url}/${endpoint}/${id}/refresh`, null, this.httpOptions)
      .pipe(
        map((data: any) => data as T),
        tap((data) => this.trackApi('response', data)),
        catchError(this.handleResourceError<T>('refresh'))
      );
  }

  keepAlive() {
    const url = this.url.replace(
      'car/api/v1',
      'dlg-gateway-routing/sso/auth/session'
    );
    return this.httpClient.get(`${url}/keepAlive`, this.httpOptions).pipe(
      map((data: any) => data as T),
      catchError(this.handleResourceError<T>('keepAlive'))
    );
  }

  list(listPropName: string = null): Observable<T[]> {
    this.trackApi('request');
    return this.httpClient
      .get(`${this.url}/${this.endpoint}`, this.httpOptions)
      .pipe(
        map((data: any) => (listPropName !== null ? data[listPropName] : data)),
        tap((data) => this.trackApi('response', data)),
        catchError(this.handleResourceError<T>('list'))
      );
  }

  update(item: T): Observable<T> {
    this.trackApi('request');
    return this.httpClient
      .put<T>(`${this.url}/${this.endpoint}`, item, this.httpOptions)
      .pipe(
        map((data) => data),
        tap((data) => this.trackApi('response', data)),
        catchError(this.handleResourceError<T>('update'))
      );
  }

  delete(id: number): Observable<T> {
    this.trackApi('request');
    return this.httpClient.delete(`${this.url}/${this.endpoint}/${id}`).pipe(
      map((data) => data as T),
      tap((data) => this.trackApi('response', data)),
      catchError(this.handleResourceError<T>('delete'))
    );
  }

  private handleResourceError<U>(
    operation = 'operation'
  ): (error: any) => Observable<U> {
    return (error: any): Observable<U> => {
      console.error(operation, error);
      AnalyticsService.trackApi({
        name: this.endpoint,
        error: error?.message,
        call_type: 'failed',
        response_value: false,
      });
      return throwError(error);
    };
  }

  private trackApi(apiCallType: 'request' | 'response', res = null) {
    const dlnNotValidated =
      (res?.getDVLAInfoResponse?.driver ?? [])[0]?.getDVLAInfoResponse
        ?.isDlnValidated === false
        ? 'DLN not validated'
        : null;

    const addrLookupStatus =
      res?.getAddressFromPostcodeResponse?.Status?.StatusDescription;
    const addLookupError =
      addrLookupStatus !== 'Successful' ? addrLookupStatus : null;

    const vrnLookupError =
      res?.VRNLookupResponse?.BusinessError?.BusinessErrorDescription;

    const error = vrnLookupError ?? addLookupError ?? dlnNotValidated;

    AnalyticsService.trackApi({
      name: this.endpoint,
      call_type: apiCallType,
      response_value: !!res,
      ...(error && { error }),
    });
  }
}
